import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Sparkles, RefreshCw, Loader2, TrendingUp, AlertTriangle,
  Clock, MessageCircle, BarChart3, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_COLORS = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-blue-100 text-blue-800',
};

export const AIInsightsPanel = ({ qrId }) => {
  const { token } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, [qrId]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsights(res.data);
    } catch {
      // No data yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!insights || insights.total_submissions === 0) {
    return (
      <Card className="p-6 border border-[#E2E8F0]" data-testid="ai-insights-panel">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Conversational Intelligence
          </h2>
        </div>
        <p className="text-sm text-[#475569] text-center py-6">
          Complete some interviews to unlock AI-powered insights, themes, and pattern analysis.
        </p>
      </Card>
    );
  }

  const { recurring_themes, pain_points, urgency_patterns, sentiment_approximation, key_findings } = insights;

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="ai-insights-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Conversational Intelligence
          </h2>
          <span className="text-xs bg-[#EFF6FF] text-[#1d4ed8] px-2 py-0.5 rounded-full font-medium">
            AI-Generated
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchInsights}
          className="rounded-full"
          data-testid="refresh-insights-btn"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Overall Summary */}
      {insights.summary && (
        <div className="p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] mb-5" data-testid="insights-summary">
          <p className="text-sm text-[#334155]">{insights.summary}</p>
          <p className="text-[10px] text-[#94A3B8] mt-1">Based on {insights.total_submissions} completed interviews</p>
        </div>
      )}

      <div className="space-y-5">
        {/* Key Findings */}
        {key_findings?.length > 0 && (
          <div data-testid="key-findings-section">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="h-4 w-4 text-[#1d4ed8]" /> Key Findings
            </h3>
            <div className="space-y-1.5">
              {key_findings.map((f, i) => (
                <div key={`item-${i}`} className="flex items-start gap-2 text-xs text-[#334155]">
                  <span className="text-[#1d4ed8] font-bold shrink-0">{i+1}.</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recurring Themes */}
        {recurring_themes?.length > 0 && (
          <div data-testid="recurring-themes-section">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageCircle className="h-4 w-4 text-purple-600" /> Recurring Themes
            </h3>
            <div className="space-y-2">
              {recurring_themes.map((t, i) => (
                <div key={`item-${i}`} className="p-3 rounded-lg border border-[#E2E8F0] bg-purple-50/30">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{t.theme}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                      {t.frequency}x mentioned
                    </span>
                  </div>
                  {t.evidence && <p className="text-xs text-[#475569]">{t.evidence}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pain Points */}
        {pain_points?.length > 0 && (
          <div data-testid="pain-points-section">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className="h-4 w-4 text-red-500" /> Top Pain Points
            </h3>
            <div className="space-y-2">
              {pain_points.map((p, i) => (
                <div key={`item-${i}`} className="p-3 rounded-lg border border-[#E2E8F0] bg-red-50/20">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium flex-1">{p.point}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLORS[p.severity] || SEVERITY_COLORS.medium}`}>
                      {p.severity}
                    </span>
                    <span className="text-[10px] text-[#475569]">{p.mentions} mentions</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Urgency Patterns */}
        {urgency_patterns?.length > 0 && (
          <div data-testid="urgency-patterns-section">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Clock className="h-4 w-4 text-amber-600" /> Urgency Patterns
            </h3>
            <div className="space-y-2">
              {urgency_patterns.map((u, i) => (
                <div key={`item-${i}`} className="p-3 rounded-lg border border-amber-200 bg-amber-50/30">
                  <p className="text-sm font-medium">{u.pattern}</p>
                  {u.indicator && <p className="text-xs text-[#475569] mt-0.5">Signal: {u.indicator}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sentiment Approximation */}
        {sentiment_approximation && (
          <div data-testid="sentiment-section">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-4 w-4 text-teal-600" /> Sentiment Approximation
            </h3>
            <div className="p-3 rounded-lg border border-teal-200 bg-teal-50/30">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm font-medium capitalize">{sentiment_approximation.tone}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  sentiment_approximation.confidence === 'high' ? 'bg-green-100 text-green-700' :
                  sentiment_approximation.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {sentiment_approximation.confidence} confidence
                </span>
              </div>
              {sentiment_approximation.note && (
                <p className="text-xs text-[#475569] flex items-start gap-1">
                  <Info className="h-3 w-3 shrink-0 mt-0.5" />
                  {sentiment_approximation.note}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Lead Distribution */}
        {insights.lead_distribution && (
          <div data-testid="lead-distribution-section">
            <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Qualification Distribution
            </h3>
            <div className="flex gap-3">
              {Object.entries(insights.lead_distribution)
                .filter(([k]) => k !== 'none')
                .map(([label, count]) => (
                  <div key={label} className={`flex-1 p-3 rounded-lg text-center border ${
                    label === 'hot' ? 'bg-red-50 border-red-200' :
                    label === 'warm' ? 'bg-amber-50 border-amber-200' :
                    'bg-blue-50 border-blue-200'
                  }`}>
                    <p className="text-lg font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{count}</p>
                    <p className={`text-xs font-medium capitalize ${
                      label === 'hot' ? 'text-red-700' : label === 'warm' ? 'text-amber-700' : 'text-blue-700'
                    }`}>{label}</p>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Disclaimer */}
      <div className="mt-4 pt-3 border-t border-[#E2E8F0]">
        <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
          <Info className="h-3 w-3" />
          AI-generated analysis based on interview response data. All insights are grounded in actual submissions.
          {insights.generated_at && ` Generated: ${new Date(insights.generated_at).toLocaleString()}`}
        </p>
      </div>
    </Card>
  );
};
